import { flash, Sidebar } from "@xola/ui-kit";
import { startsWith } from "lodash";
import React, { ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { useMenuSidebarStore } from "@/stores/menuSidebarStore";

interface MenuItemProps {
    isDisabled?: boolean; // TODO: This is only temporary till menu items are implemented
    hideWhenEmpty?: boolean; // Hide menu item when there's no content or plugin sub items
    label: string;
    route?: string;
    icon?: ReactElement | ReactNode;
    isSubMenuItem?: boolean;
    content?: ReactNode;
}

export const MenuItem = ({
    route,
    icon,
    label,
    isDisabled = false,
    hideWhenEmpty = false,
    isSubMenuItem = false,
    content,
}: MenuItemProps) => {
    const [shouldStayOpen, setShouldStayOpen] = useState<boolean>();
    const iframeContentRef = useRef<HTMLCollectionOf<Element>>();

    const history = useHistory();
    const location = useLocation();
    const currentRoute = location.pathname;

    const handleClick = () => {
        if (route && !isDisabled) {
            history.push(`/${route}`);
        }

        if (isDisabled) {
            flash.caution("Route not implemented");
        }
    };

    const handleShouldCloseTippy = useDebouncedCallback(() => {
        setShouldStayOpen(false);
        setShouldStayOpen(undefined);
    }, 50);

    const isActiveRoute = (routePart: string) => {
        return startsWith(currentRoute, `/${routePart}`);
    };

    // This is the root menu item on which the sub-menus may or may not exist
    const isRouteActive = !!(route && isActiveRoute(route));
    const sidebarLink = (
        <Sidebar.Link
            icon={icon}
            isActive={isRouteActive}
            isSubMenuItem={isSubMenuItem}
            align="left"
            classNames={{ text: isSubMenuItem ? undefined : "truncate overflow-ellipsis !w-25 leading-p1" }}
            onClick={() => useMenuSidebarStore.setState({ shouldCloseDrawer: true })}
        >
            {label}
        </Sidebar.Link>
    );

    // Track iframe content changes, cos TippyJs not closed if we hover over iframe content. To handle this we use handleShouldCloseTippy
    useEffect(() => {
        const updateIframeContent = () => {
            // eslint-disable-next-line unicorn/prefer-query-selector
            iframeContentRef.current = document.getElementsByClassName("iframe-content");
            const iframe = iframeContentRef.current?.[0];

            if (iframe) {
                iframe.addEventListener("mouseenter", handleShouldCloseTippy);
            }
        };

        // Initial setup
        updateIframeContent();

        // Use MutationObserver to detect changes in the DOM
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === "childList") {
                    updateIframeContent();
                }
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
            const iframe = iframeContentRef.current?.[0];
            if (iframe) {
                iframe.removeEventListener("mouseenter", handleShouldCloseTippy);
                handleShouldCloseTippy.cancel();
            }
        };
    }, [handleShouldCloseTippy]);

    if (hideWhenEmpty && !content) {
        return null;
    }

    return content ? (
        <span>
            <Sidebar.Menu
                visible={shouldStayOpen}
                content={
                    <div className="space-y-2 py-6">
                        <Sidebar.Heading icon={icon} label={label} />
                        <div>
                            {content} {/* Any existing "manually" added menu items */}
                        </div>
                    </div>
                }
                onMount={(instance: any) => {
                    const element = instance.popper;
                    if (!element) {
                        return;
                    }

                    const clickHandler = (e: any) => {
                        instance.hide();
                        e.stopPropagation();
                        useMenuSidebarStore.setState({ shouldCloseDrawer: true });
                    };

                    element.addEventListener("click", clickHandler);

                    // Save handler for cleanup
                    instance._clickHandler = clickHandler;
                }}
                onHide={(instance: any) => {
                    const element = instance.popper;
                    if (!element || !instance._clickHandler) {
                        return;
                    }

                    element.removeEventListener("click", instance._clickHandler);
                }}
            >
                <span>{sidebarLink}</span>
            </Sidebar.Menu>
        </span>
    ) : (
        // Regular link with no *CUSTOM* submenu items
        <span onClick={handleClick}>{sidebarLink}</span>
    );
};
